import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminService, profileService } from '../../services/api';
import '../../styles/Admin.css';

const ProfileDetails = () => {
  const params = useParams();
  const { profileId, userId } = params;
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [success, setSuccess] = useState(null);
  
  // Vérifier si l'utilisateur est admin
  useEffect(() => {
    if (!currentUser || !currentUser.isAdmin) {
      navigate('/');
    } else if (profileId || userId) {
      loadProfile();
    }
  }, [currentUser, navigate, profileId, userId]);
  
  // Charger les détails du profil
  const loadProfile = async () => {
    try {
      console.log('Début du chargement du profil...');
      console.log('profileId:', profileId);
      console.log('userId:', userId);
      
      setLoading(true);
      setError(null);
      let data = null;

      if (profileId) {
        console.log('Récupération du profil par ID:', profileId);
        data = await profileService.getProfileById(profileId);
        console.log('Résultat de getProfileById:', data);
      } else if (userId) {
        console.log('Récupération du profil par user ID:', userId);
        data = await profileService.getProfileByUserId(userId);
        console.log('Résultat de getProfileByUserId:', data);
      } else {
        const errorMsg = 'Aucun identifiant de profil ou d\'utilisateur fourni';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      if (!data) {
        const errorMsg = 'Profil non trouvé';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      console.log('Profil chargé avec succès:', data);
      setProfile(data);

      // Initialiser le formulaire avec les données du profil
      setFormData({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        pronouns: data.pronouns || '',
        titles: data.titles || [],
        expertise: data.expertise || [],
        networks: data.networks || [],
        websites: data.websites || [],
        socialMedia: data.socialMedia || [],
        languages: data.languages || [],
        datasets: data.datasets || [],
        projects: data.projects || [],
        infrastructure: data.infrastructure || [],
        documents: data.documents || []
      });
    } catch (err) {
      console.error('Erreur lors du chargement du profil:', err);
      setError(err.message || 'Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  // Gérer les changements dans le formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Gérer les changements dans les tableaux simples (expertise, languages, networks)
  const handleArrayChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({
      ...formData,
      [field]: newArray
    });
  };

  // Ajouter un élément à un tableau simple
  const handleAddArrayItem = (field) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], '']
    });
  };

  // Supprimer un élément d'un tableau simple
  const handleRemoveArrayItem = (field, index) => {
    const newArray = [...formData[field]];
    newArray.splice(index, 1);
    setFormData({
      ...formData,
      [field]: newArray
    });
  };

  // Gérer les changements dans les tableaux d'objets (titles, websites, etc.)
  const handleObjectArrayChange = (field, index, key, value) => {
    const newArray = [...formData[field]];
    newArray[index] = {
      ...newArray[index],
      [key]: value
    };
    setFormData({
      ...formData,
      [field]: newArray
    });
  };

  // Ajouter un élément à un tableau d'objets
  const handleAddObjectArrayItem = (field, template) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], template]
    });
  };

  // Supprimer un élément d'un tableau d'objets
  const handleRemoveObjectArrayItem = (field, index) => {
    const newArray = [...formData[field]];
    newArray.splice(index, 1);
    setFormData({
      ...formData,
      [field]: newArray
    });
  };

  // Soumettre les modifications
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      console.log('Soumission des modifications du profil', formData);

      // Déterminer l'ID à utiliser pour la mise à jour
      let targetUserId;

      if (userId) {
        // Si nous avons un userId dans l'URL, l'utiliser directement
        targetUserId = userId;
        console.log('Mise à jour du profil avec l\'ID utilisateur de l\'URL:', targetUserId);
      } else if (profile && profile.user) {
        // Sinon, utiliser l'ID utilisateur associé au profil
        if (typeof profile.user === 'object' && profile.user._id) {
          targetUserId = profile.user._id;
        } else if (typeof profile.user === 'string') {
          targetUserId = profile.user;
        }
        console.log('Mise à jour du profil avec l\'ID utilisateur du profil:', targetUserId);
      } else {
        throw new Error('Impossible de déterminer l\'ID utilisateur pour la mise à jour');
      }

      if (!targetUserId) {
        throw new Error('ID utilisateur non valide');
      }

      // Vérifier que le formData contient toutes les propriétés requises
      if (!formData.firstName || !formData.lastName) {
        throw new Error('Les champs Prénom et Nom sont obligatoires');
      }

      // Mettre à jour le profil
      const updatedProfile = await adminService.updateUserProfile(targetUserId, formData);
      console.log('Profil mis à jour avec succès:', updatedProfile);

      // Recharger le profil
      await loadProfile();

      setIsEditing(false);
      setSuccess('Profil mis à jour avec succès');

      // Masquer le message de succès après 3 secondes
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Erreur lors de la mise à jour du profil');
      console.error('Erreur lors de la mise à jour du profil:', err);
    } finally {
      setLoading(false);
    }
  };

  // Approuver un profil
  const handleApproveProfile = async () => {
    try {
      console.log('Début de l\'approbation du profil...');
      console.log('Profil ID:', profile?._id);
      
      if (!profile?._id) {
        const errorMsg = 'ID de profil manquant';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }
      
      setLoading(true);
      setError(null);
      setSuccess(null);

      console.log('Appel à adminService.approveProfile avec ID:', profile._id);
      const result = await adminService.approveProfile(profile._id);
      console.log('Résultat de l\'approbation:', result);

      // Recharger le profil
      console.log('Rechargement du profil après approbation...');
      await loadProfile();

      const successMsg = 'Profil approuvé avec succès';
      console.log(successMsg);
      setSuccess(successMsg);

      // Masquer le message de succès après 3 secondes
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      const errorMsg = err.message || 'Erreur lors de l\'approbation du profil';
      console.error('Erreur lors de l\'approbation:', err);
      setError(errorMsg);
      
      // Si le profil n'existe plus, recharger la liste des profils
      if (err.response?.status === 404) {
        console.log('Profil non trouvé (404), redirection vers la liste des profils...');
        setTimeout(() => {
          navigate('/admin/profiles');
        }, 2000);
      }
    } finally {
      setLoading(false);
      setShowApprovalModal(false);
    }
  };

  // Rejeter un profil
  const handleRejectProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      if (!profile || !profile._id) {
        throw new Error('Profil non valide');
      }

      console.log('Rejet du profil avec ID:', profile._id, 'et raison:', rejectReason);
      await adminService.rejectProfile(profile._id, rejectReason);

      // Recharger le profil
      await loadProfile();

      setShowRejectModal(false);
      setRejectReason('');
      setSuccess('Profil rejeté avec succès');

      // Masquer le message de succès après 3 secondes
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Erreur lors du rejet du profil');
      console.error('Erreur lors du rejet du profil:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fermer les modals
  const closeModals = () => {
    setShowApprovalModal(false);
    setShowRejectModal(false);
    setRejectReason('');
  };

  // Obtenir le statut du profil sous forme de texte
  const getProfileStatus = (status) => {
    switch (status) {
      case 'pending':
        return 'En attente d\'approbation';
      case 'approved':
        return 'Approuvé';
      case 'rejected':
        return 'Rejeté';
      default:
        return 'Inconnu';
    }
  };

  // Afficher un écran de chargement pendant le chargement des données
  if (loading) {
    return (
      <div className="admin-profile-details">
        <div className="container">
          <div className="loading">Chargement du profil...</div>
        </div>
      </div>
    );
  }
  
  // Afficher un message d'erreur si le profil n'a pas été trouvé
  if (!profile) {
    return (
      <div className="admin-profile-details">
        <div className="container">
          <div className="page-header">
            <h1 className="page-title">Détails du profil</h1>
            <div className="header-actions">
              <button
                className="btn btn-outline"
                onClick={() => navigate('/admin/profiles')}
              >
                Retour à la liste
              </button>
            </div>
          </div>
          <div className="alert alert-warning">
            <p>Profil non trouvé. Il est possible que :</p>
            <ul>
              <li>Le profil n'existe pas dans la base de données</li>
              <li>L'utilisateur n'a pas encore créé de profil</li>
              <li>L'identifiant du profil ou de l'utilisateur est incorrect</li>
            </ul>
            <p>Veuillez vérifier l'identifiant du profil ou de l'utilisateur et réessayer.</p>
          </div>
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-profile-details">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">
            Détails du profil: {profile.firstName} {profile.lastName}
            {profile.status && (
              <span className={`status-badge ${profile.status}`}>
                {getProfileStatus(profile.status)}
              </span>
            )}
          </h1>
          <div className="header-actions">
            <button
              className="btn btn-outline"
              onClick={() => navigate('/admin/profiles')}
            >
              Retour à la liste
            </button>

            {!isEditing ? (
              <>
                {/* Boutons d'approbation/rejet pour les profils en attente */}
                {profile.status === 'pending' && (
                  <div className="approval-actions">
                    <button
                      className="btn btn-success"
                      onClick={() => setShowApprovalModal(true)}
                    >
                      <i className="fas fa-check"></i> Approuver
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => setShowRejectModal(true)}
                    >
                      <i className="fas fa-times"></i> Rejeter
                    </button>
                  </div>
                )}

                {/* Bouton pour réapprouver un profil rejeté */}
                {profile.status === 'rejected' && (
                  <button
                    className="btn btn-success"
                    onClick={() => setShowApprovalModal(true)}
                  >
                    <i className="fas fa-check"></i> Réapprouver
                  </button>
                )}

                {/* Bouton d'édition pour tous les profils */}
                <button
                  className="btn btn-primary"
                  onClick={() => setIsEditing(true)}
                >
                  <i className="fas fa-edit"></i> Modifier
                </button>
              </>
            ) : (
              <button
                className="btn btn-outline"
                onClick={() => {
                  setIsEditing(false);
                  loadProfile(); // Recharger les données originales
                }}
              >
                Annuler
              </button>
            )}
          </div>
        </div>

        {/* Messages d'état */}
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {profile && (
          <div className="profile-container">
            <div className="profile-header">
              <div className="profile-status">
                <h3>Statut: {getProfileStatus(profile.status)}</h3>
                {profile.status === 'rejected' && profile.rejectionReason && (
                  <div className="rejection-reason">
                    <p>
                      <strong>Raison du rejet:</strong>
                    </p>
                    <p>{profile.rejectionReason}</p>
                  </div>
                )}
              </div>

              <div className="profile-actions">
                {profile.status !== 'approved' && (
                  <button
                    className="btn btn-success"
                    onClick={() => setShowApprovalModal(true)}
                  >
                    Approuver
                  </button>
                )}
                {profile.status !== 'rejected' && (
                  <button
                    className="btn btn-danger"
                    onClick={() => setShowRejectModal(true)}
                  >
                    Rejeter
                  </button>
                )}
              </div>
            </div>

            {isEditing ? (
              <form onSubmit={handleSubmit} className="profile-edit-form">
                <div className="form-section">
                  <h2>Informations personnelles</h2>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">Prénom</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Nom</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="pronouns">Pronoms</label>
                    <input
                      type="text"
                      id="pronouns"
                      name="pronouns"
                      value={formData.pronouns || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                
                <div className="form-section">
                  <h2>Titres et affiliations</h2>
                  {formData.titles.map((title, index) => (
                    <div key={index} className="array-item">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Titre</label>
                          <input
                            type="text"
                            value={title.title}
                            onChange={(e) => handleObjectArrayChange('titles', index, 'title', e.target.value)}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Organisation</label>
                          <input
                            type="text"
                            value={title.organization}
                            onChange={(e) => handleObjectArrayChange('titles', index, 'organization', e.target.value)}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Département</label>
                        <input
                          type="text"
                          value={title.department || ''}
                          onChange={(e) => handleObjectArrayChange('titles', index, 'department', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Unité</label>
                        <input
                          type="text"
                          value={title.unit || ''}
                          onChange={(e) => handleObjectArrayChange('titles', index, 'unit', e.target.value)}
                          placeholder="Optionnel"
                        />
                      </div>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-danger remove-btn"
                        onClick={() => handleRemoveObjectArrayItem('titles', index)}
                      >
                        Supprimer
                      </button>
                    </div>
                  ))}
                  <button 
                    type="button" 
                    className="btn btn-outline add-btn"
                    onClick={() => handleAddObjectArrayItem('titles', { title: '', organization: '', department: '', unit: '' })}
                  >
                    Ajouter un titre
                  </button>
                </div>
                
                <div className="form-section">
                  <h2>Expertises</h2>
                  {formData.expertise.map((exp, index) => (
                    <div key={index} className="array-item">
                      <div className="form-group">
                        <input
                          type="text"
                          value={exp}
                          onChange={(e) => handleArrayChange('expertise', index, e.target.value)}
                          required
                        />
                      </div>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-danger remove-btn"
                        onClick={() => handleRemoveArrayItem('expertise', index)}
                      >
                        Supprimer
                      </button>
                    </div>
                  ))}
                  <button 
                    type="button" 
                    className="btn btn-outline add-btn"
                    onClick={() => handleAddArrayItem('expertise')}
                  >
                    Ajouter une expertise
                  </button>
                </div>
                
                <div className="form-section">
                  <h2>Langues</h2>
                  {formData.languages.map((lang, index) => (
                    <div key={index} className="array-item">
                      <div className="form-group">
                        <input
                          type="text"
                          value={lang}
                          onChange={(e) => handleArrayChange('languages', index, e.target.value)}
                        />
                      </div>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-danger remove-btn"
                        onClick={() => handleRemoveArrayItem('languages', index)}
                      >
                        Supprimer
                      </button>
                    </div>
                  ))}
                  <button 
                    type="button" 
                    className="btn btn-outline add-btn"
                    onClick={() => handleAddArrayItem('languages')}
                  >
                    Ajouter une langue
                  </button>
                </div>
                
                <div className="form-section">
                  <h2>Réseaux sociaux</h2>
                  {formData.socialMedia.map((social, index) => (
                    <div key={index} className="array-item">
                      <div className="form-group">
                        <label>Plateforme</label>
                        <select
                          value={social.platform}
                          onChange={(e) => handleObjectArrayChange('socialMedia', index, 'platform', e.target.value)}
                          className="form-control"
                        >
                          <option value="LinkedIn">LinkedIn</option>
                          <option value="Facebook">Facebook</option>
                          <option value="X">X (Twitter)</option>
                          <option value="Instagram">Instagram</option>
                          <option value="ResearchGate">ResearchGate</option>
                          <option value="GitHub">GitHub</option>
                          <option value="YouTube">YouTube</option>
                          <option value="Autre">Autre</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>URL</label>
                        <input
                          type="url"
                          value={social.url}
                          onChange={(e) => handleObjectArrayChange('socialMedia', index, 'url', e.target.value)}
                          placeholder="https://example.com/profile"
                          required
                        />
                      </div>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-danger remove-btn"
                        onClick={() => handleRemoveObjectArrayItem('socialMedia', index)}
                      >
                        Supprimer
                      </button>
                    </div>
                  ))}
                  <button 
                    type="button" 
                    className="btn btn-outline add-btn"
                    onClick={() => handleAddObjectArrayItem('socialMedia', { platform: 'LinkedIn', url: '' })}
                  >
                    Ajouter un réseau social
                  </button>
                </div>
                
                <div className="form-section">
                  <h2>Sites web</h2>
                  {formData.websites.map((website, index) => (
                    <div key={index} className="array-item">
                      <div className="form-group">
                        <label>Nom du site</label>
                        <input
                          type="text"
                          value={website.name}
                          onChange={(e) => handleObjectArrayChange('websites', index, 'name', e.target.value)}
                          placeholder="Ex: Site institutionnel, Blog personnel"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>URL</label>
                        <input
                          type="url"
                          value={website.url}
                          onChange={(e) => handleObjectArrayChange('websites', index, 'url', e.target.value)}
                          placeholder="https://example.com"
                          required
                        />
                      </div>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-danger remove-btn"
                        onClick={() => handleRemoveObjectArrayItem('websites', index)}
                      >
                        Supprimer
                      </button>
                    </div>
                  ))}
                  <button 
                    type="button" 
                    className="btn btn-outline add-btn"
                    onClick={() => handleAddObjectArrayItem('websites', { name: '', url: '' })}
                  >
                    Ajouter un site web
                  </button>
                </div>
                
                <div className="form-section">
                  <h2>Jeux de données</h2>
                  {formData.datasets.map((dataset, index) => (
                    <div key={index} className="array-item">
                      <div className="form-group">
                        <label>Nom du jeu de données</label>
                        <input
                          type="text"
                          value={dataset.name}
                          onChange={(e) => handleObjectArrayChange('datasets', index, 'name', e.target.value)}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>URL</label>
                        <input
                          type="url"
                          value={dataset.url || ''}
                          onChange={(e) => handleObjectArrayChange('datasets', index, 'url', e.target.value)}
                          placeholder="https://example.com/dataset"
                        />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={dataset.description || ''}
                          onChange={(e) => handleObjectArrayChange('datasets', index, 'description', e.target.value)}
                          placeholder="Description du jeu de données"
                          rows="3"
                        />
                      </div>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-danger remove-btn"
                        onClick={() => handleRemoveObjectArrayItem('datasets', index)}
                      >
                        Supprimer
                      </button>
                    </div>
                  ))}
                  <button 
                    type="button" 
                    className="btn btn-outline add-btn"
                    onClick={() => handleAddObjectArrayItem('datasets', { name: '', url: '', description: '' })}
                  >
                    Ajouter un jeu de données
                  </button>
                </div>
                
                <div className="form-section">
                  <h2>Projets</h2>
                  {formData.projects.map((project, index) => (
                    <div key={index} className="array-item">
                      <div className="form-group">
                        <label>Nom du projet</label>
                        <input
                          type="text"
                          value={project.name}
                          onChange={(e) => handleObjectArrayChange('projects', index, 'name', e.target.value)}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={project.description || ''}
                          onChange={(e) => handleObjectArrayChange('projects', index, 'description', e.target.value)}
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Date de début</label>
                          <input
                            type="date"
                            value={project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : ''}
                            onChange={(e) => handleObjectArrayChange('projects', index, 'startDate', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label>Date de fin</label>
                          <input
                            type="date"
                            value={project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : ''}
                            onChange={(e) => handleObjectArrayChange('projects', index, 'endDate', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>URL</label>
                        <input
                          type="url"
                          value={project.url || ''}
                          onChange={(e) => handleObjectArrayChange('projects', index, 'url', e.target.value)}
                        />
                      </div>
                      <div className="form-group checkbox-group">
                        <input
                          id={`project-rqm-${index}`}
                          type="checkbox"
                          checked={project.fundedByRQM || false}
                          onChange={(e) => handleObjectArrayChange('projects', index, 'fundedByRQM', e.target.checked)}
                          className="form-checkbox"
                        />
                        <label htmlFor={`project-rqm-${index}`}>Projet financé par le RQM</label>
                        {project.fundedByRQM && (
                          <span className="badge badge-rqm">
                            <i className="fas fa-award"></i> RQM
                          </span>
                        )}
                      </div>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-danger remove-btn"
                        onClick={() => handleRemoveObjectArrayItem('projects', index)}
                      >
                        Supprimer
                      </button>
                    </div>
                  ))}
                  <button 
                    type="button" 
                    className="btn btn-outline add-btn"
                    onClick={() => handleAddObjectArrayItem('projects', { name: '', description: '', startDate: '', endDate: '', url: '', fundedByRQM: false })}
                  >
                    Ajouter un projet
                  </button>
                </div>
                
                <div className="form-section">
                  <h2>Infrastructures de recherche</h2>
                  {formData.infrastructure.map((infra, index) => (
                    <div key={index} className="array-item">
                      <div className="form-group">
                        <label>Nom de l'infrastructure</label>
                        <input
                          type="text"
                          value={infra.name}
                          onChange={(e) => handleObjectArrayChange('infrastructure', index, 'name', e.target.value)}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>URL</label>
                        <input
                          type="url"
                          value={infra.url || ''}
                          onChange={(e) => handleObjectArrayChange('infrastructure', index, 'url', e.target.value)}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={infra.description || ''}
                          onChange={(e) => handleObjectArrayChange('infrastructure', index, 'description', e.target.value)}
                          rows="3"
                        />
                      </div>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-danger remove-btn"
                        onClick={() => handleRemoveObjectArrayItem('infrastructure', index)}
                      >
                        Supprimer
                      </button>
                    </div>
                  ))}
                  <button 
                    type="button" 
                    className="btn btn-outline add-btn"
                    onClick={() => handleAddObjectArrayItem('infrastructure', { name: '', url: '', description: '' })}
                  >
                    Ajouter une infrastructure
                  </button>
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn btn-outline"
                    onClick={() => {
                      setIsEditing(false);
                      loadProfile(); // Recharger les données originales
                    }}
                  >
                    Annuler
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="profile-details">
                <div className="profile-section">
                  <h2>Informations personnelles</h2>
                  <div className="detail-item">
                    <span className="detail-label">Nom complet:</span>
                    <span className="detail-value">{profile.firstName} {profile.lastName}</span>
                  </div>
                  {profile.pronouns && (
                    <div className="detail-item">
                      <span className="detail-label">Pronoms:</span>
                      <span className="detail-value">{profile.pronouns}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{profile.user?.email}</span>
                  </div>
                </div>
                
                {profile.titles && profile.titles.length > 0 && (
                  <div className="profile-section">
                    <h2>Titres et affiliations</h2>
                    {profile.titles.map((title, index) => (
                      <div key={index} className="detail-card">
                        <div className="detail-item">
                          <span className="detail-label">Titre:</span>
                          <span className="detail-value">{title.title}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Organisation:</span>
                          <span className="detail-value">{title.organization}</span>
                        </div>
                        {title.department && (
                          <div className="detail-item">
                            <span className="detail-label">Département:</span>
                            <span className="detail-value">{title.department}</span>
                          </div>
                        )}
                        {title.unit && (
                          <div className="detail-item">
                            <span className="detail-label">Unité:</span>
                            <span className="detail-value">{title.unit}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {profile.expertise && profile.expertise.length > 0 && (
                  <div className="profile-section">
                    <h2>Expertises</h2>
                    <div className="tags-container">
                      {profile.expertise.map((exp, index) => (
                        <span key={index} className="tag">{exp}</span>
                      ))}
                    </div>
                  </div>
                )}
                
                {profile.languages && profile.languages.length > 0 && (
                  <div className="profile-section">
                    <h2>Langues</h2>
                    <div className="tags-container">
                      {profile.languages.map((lang, index) => (
                        <span key={index} className="tag">{lang}</span>
                      ))}
                    </div>
                  </div>
                )}
                
                {profile.websites && profile.websites.length > 0 && (
                  <div className="profile-section">
                    <h2>Sites web</h2>
                    {profile.websites.map((website, index) => (
                      <div key={index} className="detail-card">
                        <div className="detail-item">
                          <span className="detail-label">Nom:</span>
                          <span className="detail-value">{website.name}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">URL:</span>
                          <a href={website.url} target="_blank" rel="noopener noreferrer" className="detail-link">
                            {website.url}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {profile.socialMedia && profile.socialMedia.length > 0 && (
                  <div className="profile-section">
                    <h2>Réseaux sociaux</h2>
                    {profile.socialMedia.map((social, index) => (
                      <div key={index} className="detail-card">
                        <div className="detail-item">
                          <span className="detail-label">Plateforme:</span>
                          <span className="detail-value">{social.platform}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">URL:</span>
                          <a href={social.url} target="_blank" rel="noopener noreferrer" className="detail-link">
                            {social.url}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {profile.projects && profile.projects.length > 0 && (
                  <div className="profile-section">
                    <h2>Projets</h2>
                    {profile.projects.map((project, index) => (
                      <div key={index} className="detail-card">
                        <div className="detail-item">
                          <span className="detail-label">Nom:</span>
                          <span className="detail-value">{project.name}</span>
                        </div>
                        {project.description && (
                          <div className="detail-item">
                            <span className="detail-label">Description:</span>
                            <span className="detail-value">{project.description}</span>
                          </div>
                        )}
                        {(project.startDate || project.endDate) && (
                          <div className="detail-item">
                            <span className="detail-label">Période:</span>
                            <span className="detail-value">
                              {project.startDate ? new Date(project.startDate).toLocaleDateString() : 'N/A'} - 
                              {project.endDate ? new Date(project.endDate).toLocaleDateString() : 'En cours'}
                            </span>
                          </div>
                        )}
                        {project.url && (
                          <div className="detail-item">
                            <span className="detail-label">URL:</span>
                            <a href={project.url} target="_blank" rel="noopener noreferrer" className="detail-link">
                              {project.url}
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {profile.datasets && profile.datasets.length > 0 && (
                  <div className="profile-section">
                    <h2>Jeux de données</h2>
                    {profile.datasets.map((dataset, index) => (
                      <div key={index} className="detail-card">
                        <div className="detail-item">
                          <span className="detail-label">Nom:</span>
                          <span className="detail-value">{dataset.name}</span>
                        </div>
                        {dataset.description && (
                          <div className="detail-item">
                            <span className="detail-label">Description:</span>
                            <span className="detail-value">{dataset.description}</span>
                          </div>
                        )}
                        {dataset.url && (
                          <div className="detail-item">
                            <span className="detail-label">URL:</span>
                            <a href={dataset.url} target="_blank" rel="noopener noreferrer" className="detail-link">
                              {dataset.url}
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {profile.infrastructure && profile.infrastructure.length > 0 && (
                  <div className="profile-section">
                    <h2>Infrastructures de recherche</h2>
                    {profile.infrastructure.map((infra, index) => (
                      <div key={index} className="detail-card">
                        <div className="detail-item">
                          <span className="detail-label">Nom:</span>
                          <span className="detail-value">{infra.name}</span>
                        </div>
                        {infra.description && (
                          <div className="detail-item">
                            <span className="detail-label">Description:</span>
                            <span className="detail-value">{infra.description}</span>
                          </div>
                        )}
                        {infra.url && (
                          <div className="detail-item">
                            <span className="detail-label">URL:</span>
                            <a href={infra.url} target="_blank" rel="noopener noreferrer" className="detail-link">
                              {infra.url}
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Modales d'approbation et de rejet */}
        {showApprovalModal && (
          <div className="modal-backdrop">
            <div className="modal">
              <div className="modal-header">
                <h3>Confirmer l'approbation</h3>
                <button
                  className="close-btn"
                  onClick={() => setShowApprovalModal(false)}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <p>
                  Êtes-vous sûr de vouloir approuver ce profil?
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline"
                  onClick={() => setShowApprovalModal(false)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleApproveProfile}
                  disabled={loading}
                >
                  {loading ? 'Approbation...' : 'Approuver'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de rejet */}
        {showRejectModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>Rejeter le profil</h2>
                <button className="close-btn" onClick={closeModals}>&times;</button>
              </div>
              <div className="modal-body">
                <p>Veuillez indiquer la raison du rejet du profil de {profile.firstName} {profile.lastName}:</p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Raison du rejet..."
                  className="reject-reason-textarea"
                  rows={4}
                />
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-outline"
                  onClick={closeModals}
                >
                  Annuler
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={handleRejectProfile}
                  disabled={!rejectReason.trim() || loading}
                >
                  Rejeter
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileDetails;
